import { Fragment, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faEye,
  faSave,
  faTimesCircle,
} from "@fortawesome/free-solid-svg-icons";
import styled from "styled-components";
import { TermTemplatesDetailProps } from "../../types/termTemplates";
import Editor from "./Editor";
import TermTemplatesPreviewModal from "./TermTemplatesPreviewModal";

/* --- Styled Components --- */

const FormContainer = styled.div`
  background: #ffffff;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const FormHeader = styled.div`
  padding: 1.25rem 2rem;
  border-bottom: 1px solid #f1f5f9;
  background-color: #f8fafc;
  display: flex;
  justify-content: space-between;
  align-items: center;

  .back-button {
    display: flex;
    align-items: center;
    gap: 8px;
    background: transparent;
    border: none;
    color: #64748b;
    font-weight: 600;
    cursor: pointer;
    &:hover {
      color: #044dac;
    }
  }

  .dropdown-toggle {
    background: transparent !important;
    border: none !important;
    color: #94a3b8 !important;
    padding: 8px;
    box-shadow: none !important;
    &:after {
      display: none;
    }
    &:hover {
      color: #64748b !important;
    }
  }

  .dropdown-item-delete {
    color: #ef4444;
    font-weight: 600;
    padding: 10px 16px;
    cursor: pointer;
    &:hover {
      background-color: #fef2f2;
    }
  }
`;

const FormContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem; /* 섹션 간 간격 조절 */
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column; /* 기본적으로 세로 배치 */
  gap: 0.5rem;

  .label-area {
    label {
      font-weight: 700;
      color: #334155;
      font-size: 0.95rem;
      display: flex;
      align-items: center;
      gap: 4px;
      .required {
        color: #ef4444;
      }
    }
  }

  .input-area {
    width: 100%;

    input {
      width: 100%;
      padding: 1rem 1rem;
      font-size: 0.95rem;
      border: 1px solid #cbd5e1;
      border-radius: 8px;
      transition: all 0.2s;

      &:focus {
        outline: none;
        border-color: #044dac;
        box-shadow: 0 0 0 3px rgba(4, 77, 172, 0.1);
      }

      &:disabled {
        background-color: #f8fafc;
        color: #94a3b8;
        border-color: #e2e8f0;
        cursor: not-allowed;
      }
    }
  }
`;

const ActionFooter = styled.div`
  padding: 1.5rem 2rem;
  display: flex;
  justify-content: space-between;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;

  button {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 0.75rem 1.5rem;
    font-size: 0.9rem;
    font-weight: 700;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    transition: opacity 0.2s;
    &:hover {
      opacity: 0.9;
    }
  }

  .btn-cancel {
    background-color: #dc343f;
    color: #ffffff;
  }
  .btn-save {
    background: #044dac;
    color: #ffffff;
    box-shadow: 0 4px 6px rgba(4, 77, 172, 0.2);
  }
`;

const TermTemplatesDetail = ({
  termTemplatesItem,
  userRole,
  editorRef,
  dateFormatter,
  handleChange,
  handleUpdateTermTemplates,
  handleTermTemplatesDeleteModal,
  handleGoBack,
}: TermTemplatesDetailProps) => {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  return (
    <Fragment>
      <FormContainer>
        <FormHeader>
          <button className="back-button" onClick={handleGoBack}>
            <FontAwesomeIcon icon={faArrowLeft} />
            <span>목록으로 돌아가기</span>
          </button>

          {userRole === "master" && (
            <Dropdown onClick={(e) => e.stopPropagation()}>
              <Dropdown.Toggle id="dropdown-basic">
                <div
                  className="dropdown-item-delete"
                  onClick={handleTermTemplatesDeleteModal}
                >
                  약관 삭제하기
                </div>
              </Dropdown.Toggle>
            </Dropdown>
          )}
        </FormHeader>

        <FormContent>
          {/* 1. 약관 제목 (한 줄 전체) */}
          <InputGroup>
            <div className="label-area">
              <label>
                약관 제목 <b className="required">*</b>
              </label>
            </div>
            <div className="input-area">
              <input
                name="title"
                value={termTemplatesItem.title || ""}
                onChange={handleChange}
                placeholder="약관 제목을 입력하세요."
              />
            </div>
          </InputGroup>

          <InputGroup>
            <div className="label-area">
              <label>
                약관 언어 <span className="required">*</span>
              </label>
            </div>
            <div className="input-area">
              <select
                className="card-text-select"
                value={termTemplatesItem.language}
                name="language"
                onChange={handleChange}
              >
                <option value="ko">한국어 (KO)</option>
                <option value="en">English (EN)</option>
                <option value="ja">日本語 (JA)</option>
                <option value="zh">中文 (ZH)</option>
              </select>
            </div>
          </InputGroup>

          {/* 2. 생성일자 (한 줄 전체) */}
          <InputGroup>
            <div className="label-area">
              <label>생성일자</label>
            </div>
            <div className="input-area">
              <input
                value={
                  termTemplatesItem.createdAt
                    ? dateFormatter(termTemplatesItem.createdAt)
                    : "-"
                }
                disabled
              />
            </div>
          </InputGroup>

          {/* 3. 최종 수정일자 (한 줄 전체) */}
          <InputGroup>
            <div className="label-area">
              <label>최종 수정일자</label>
            </div>
            <div className="input-area">
              <input
                value={
                  termTemplatesItem.updatedAt
                    ? dateFormatter(termTemplatesItem.updatedAt)
                    : "-"
                }
                disabled
              />
            </div>
          </InputGroup>

          {/* 4. 약관 내용 (맨 아래 전체 영역) */}
          <InputGroup>
            <div className="label-area">
              <label>
                약관 내용 <b className="required">*</b>
              </label>
            </div>
            <div className="input-area">
              <Editor
                editorRef={editorRef}
                value={termTemplatesItem.content || ""}
                onChange={(content: string) => handleChange(content)}
                hasImage
                resizable
              />

              <div style={{ display: "flex", paddingTop: "18px" }}>
                {/* 2. 미리보기 버튼 추가 */}
                <button
                  type="button"
                  style={{
                    background: "#8c9195",
                    color: "#fff",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    border: "none",
                    fontWeight: 600,
                  }}
                  onClick={() => setIsPreviewOpen(true)}
                >
                  <FontAwesomeIcon
                    icon={faEye}
                    style={{ marginRight: "6px" }}
                  />
                  미리보기
                </button>
              </div>
            </div>
          </InputGroup>
        </FormContent>

        <ActionFooter>
          <button className="btn-cancel" onClick={handleGoBack}>
            <FontAwesomeIcon icon={faTimesCircle} />
            취소
          </button>
          {userRole === "master" && (
            <button className="btn-save" onClick={handleUpdateTermTemplates}>
              <FontAwesomeIcon icon={faSave} />
              변경사항 저장하기
            </button>
          )}
        </ActionFooter>
      </FormContainer>

      <TermTemplatesPreviewModal
        show={isPreviewOpen}
        onHide={() => setIsPreviewOpen(false)}
        title={termTemplatesItem.title || ""}
        content={termTemplatesItem.content || ""}
      />
    </Fragment>
  );
};

export default TermTemplatesDetail;
